import React, { useEffect, useState } from "react";
import styled from "styled-components";
import JoinForm from "../components/join-form";
import SessionCard from "../components/session-card";
import { MadeBy } from "../components/made-by";
import { SessionRecord } from "../models/session-record";
import { getHistory } from "../hooks/use-session-history";
import logo from "../assets/copresent-logo.svg";
import { Link } from "react-router-dom";
import { beginGoogleSignIn, getStoredHostSession } from "../webAuth";
import HostDashboard from "./host-dashboard";

const Page = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: rgb(244, 244, 246);
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
`;

const Logo = styled.img`
  width: 64px;
  height: 64px;
  margin-top: 48px;
`;

const Heading = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 16px 0 0;
  text-align: center;
  max-width: 600px;
`;

const Tagline = styled.p`
  font-size: 15px;
  color: #8c959f;
  margin: 12px 0 32px;
  text-align: center;
  line-height: 1.5;
  max-width: 360px;
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: #8c959f;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 32px 0 12px;
  width: 100%;
  max-width: 400px;
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 400px;
  margin-bottom: 120px;
`;

const MadeByHusky = styled(MadeBy)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  font-size: 12px;
  text-align: center;
  padding: 24px 16px;
  background: linear-gradient(
    to top,
    rgba(244, 244, 246, 0.98) 0%,
    rgba(244, 244, 246, 0.95) 80%,
    transparent 100%
  );

  p {
    margin: 4px 0;
    line-height: 1.4;
  }

  a {
    color: #6366f1;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;

    &:hover {
      color: #4f46e5;
      text-decoration: underline;
    }
  }
`;

const HostLink = styled(Link)`
  margin-top: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #6366f1;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

// The root of the clicker. A signed-in host lands on their dashboard (the
// host controller's Home tab leads here); everyone else — copresenters with a
// code, or a host who hasn't signed in — gets the join page, which is also at
// /join for a signed-in host who wants it.
const Dashboard = () =>
  getStoredHostSession() ? <HostDashboard section="home" /> : <JoinLanding />;

export const JoinLanding = () => {
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const presenterName = localStorage.getItem("presenterName");

  useEffect(() => {
    const history = getHistory();
    if (history.length === 0) return;

    const apiUrl = process.env.REACT_APP_API_URL;

    Promise.all(
      history.map((session) =>
        fetch(`${apiUrl}/presentation/${session.magicId}/status`)
          .then((res) => (res.ok ? res.json() : null))
          .then((data) =>
            data
              ? {
                  ...session,
                  title: data.title || session.title,
                  totalSlides: data.totalSlides ?? session.totalSlides,
                  lastSlide: data.currentSlide ?? session.lastSlide,
                  online: data.online,
                }
              : null
          )
          .catch(() => null)
      )
    ).then((results) => {
      setSessions(results.filter(Boolean) as SessionRecord[]);
    });
  }, []);

  const greeting = presenterName
    ? `Welcome back, ${presenterName}`
    : "Welcome to Copresent";

  return (
    <Page>
      <Container>
        <Logo src={logo} alt="Copresent" />
        <Heading>{greeting}</Heading>
        <Tagline>Join a presentation to control slides from your phone or computer</Tagline>

        <JoinForm />
        {getStoredHostSession() ? (
          <HostLink to="/">Back to your dashboard</HostLink>
        ) : (
          <HostLink
            to="/"
            onClick={(e) => {
              e.preventDefault();
              void beginGoogleSignIn("/");
            }}
          >
            Presenting? Sign in to your dashboard
          </HostLink>
        )}

        {sessions.length > 0 && (
          <>
            <SectionTitle>Recent Presentations</SectionTitle>
            <SessionList>
              {sessions.map((session) => (
                <SessionCard key={session.magicId} session={session} />
              ))}
            </SessionList>
          </>
        )}

        <MadeByHusky />
      </Container>
    </Page>
  );
};

export default Dashboard;
